import React from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Chip} from "@mui/material";

import {black500, greyishBlue600} from "assets/jss/main";

import {formatDate, formatMoney} from "views/personalShopper/components/formatters";

import SideCard from "./SideCard";

AccountCard.propTypes = {
    account: PropTypes.shape({
        ordersCount: PropTypes.number,
        revenue: PropTypes.number,
        currencyCode: PropTypes.string,
        lastOrderAt: PropTypes.string,
        brands: PropTypes.arrayOf(PropTypes.string),
        loyalty: PropTypes.shape({
            programme: PropTypes.string,
            points: PropTypes.number,
            tier: PropTypes.string,
        }),
    }),
    accountLinked: PropTypes.bool.isRequired,
};

Figure.propTypes = {
    labelId: PropTypes.string.isRequired,
    value: PropTypes.node,
};

function Figure({labelId, value}) {
    return (
        <div style={{flex: "1 1 30%", minWidth: "90px"}}>
            <div style={{color: greyishBlue600, fontSize: "11px", textTransform: "uppercase", fontWeight: 600}}><FormattedMessage id={labelId} /></div>
            <div style={{color: black500, fontSize: "18px", fontWeight: 700}}>{value ?? "–"}</div>
        </div>
    );
}

/**
 * The site account linked to the number, as Sylius serves it: every order (not only the
 * assisted ones), the brands the shopper comes back to, and the loyalty points (Coniq) when
 * the project runs the programme and the customer is enrolled.
 */
function AccountCard({account, accountLinked}) {
    if (!accountLinked) {
        return (
            <SideCard titleId="personalShopper.account.title">
                <div style={{color: greyishBlue600, fontSize: "13px"}}><FormattedMessage id="personalShopper.account.none" /></div>
            </SideCard>
        );
    }
    if (!account) {
        return (
            <SideCard titleId="personalShopper.account.title">
                <div style={{color: greyishBlue600, fontSize: "13px"}}><FormattedMessage id="personalShopper.account.pending" /></div>
            </SideCard>
        );
    }

    return (
        <SideCard titleId="personalShopper.account.title">
            <div style={{display: "flex", flexWrap: "wrap", gap: "10px"}}>
                <Figure labelId="personalShopper.account.orders" value={account.ordersCount ?? 0} />
                <Figure labelId="personalShopper.account.revenue" value={formatMoney(account.revenue ?? 0, account.currencyCode || "EUR")} />
                <Figure labelId="personalShopper.account.loyalty" value={account.loyalty ? `${account.loyalty.points} pts` : "–"} />
            </div>
            <div style={{color: greyishBlue600, fontSize: "12px", marginTop: "8px"}}>
                {account.lastOrderAt && <FormattedMessage id="personalShopper.account.lastOrder" values={{date: formatDate(account.lastOrderAt)}} />}
                {account.loyalty?.tier && <span>{account.lastOrderAt ? " · " : ""}<FormattedMessage id="personalShopper.account.tier" values={{tier: account.loyalty.tier}} /></span>}
                {!account.loyalty && <span>{account.lastOrderAt ? " · " : ""}<FormattedMessage id="personalShopper.account.noLoyalty" /></span>}
            </div>
            {(account.brands || []).length > 0 && (
                <div style={{display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "8px"}}>
                    {account.brands.map((brand) => <Chip key={brand} size="small" variant="outlined" label={brand} />)}
                </div>
            )}
        </SideCard>
    );
}

export default AccountCard;
